import { el, toast } from '../core/dom.js';
import { getState, planFor } from '../core/state.js';
import { metaSet, planStates } from '../storage/db.js';
import { nowIso } from '../core/time.js';
import { navigate } from '../router.js';
import type {
  AppEvent, HabitRecord, IntervalState, MoneyModelRecord, OnboardingMetadata,
  QuotaState, UserSettings,
} from '../core/types.js';

export type TuneUpCardId =
  | 'improve_baseline'
  | 'add_money'
  | 'add_risky_times'
  | 'choose_reminders'
  | 'easier_harder';

export interface TuneUpContext {
  habit: HabitRecord;
  recentEvents: AppEvent[];
  money: MoneyModelRecord | undefined;
  settings: UserSettings;
  metadata: OnboardingMetadata | undefined;
}

interface TuneUpCardDef {
  id: TuneUpCardId;
  /** Stable order — first eligible card wins. */
  priority: number;
  isEligible: (ctx: TuneUpContext) => boolean;
  build: (ctx: TuneUpContext, onAction: (completed: boolean) => Promise<void>) => HTMLElement;
}

const CARDS: TuneUpCardDef[] = [
  {
    id: 'improve_baseline',
    priority: 10,
    isEligible: (ctx) => {
      const uses = ctx.recentEvents.filter(
        (e) => e.type === 'usage_logged' && e.status !== 'undone',
      ).length;
      const calibration = ctx.metadata?.baselineConfidence === 'unknown';
      return uses >= 3 && (calibration || ctx.metadata?.baselineConfidence === 'estimated');
    },
    build: (ctx, onAction) => buildCard({
      title: 'Improve your baseline?',
      body: 'Were the first few logs close to your usual pattern?',
      actions: [
        { label: 'Close enough', onClick: async () => { await onAction(true); } },
        { label: 'I usually do more', onClick: async () => {
          await adjustBaseline(ctx.habit.id, 'more');
          await onAction(true);
          toast('Baseline relaxed — gentler targets.');
        } },
        { label: 'I usually do less', onClick: async () => {
          await adjustBaseline(ctx.habit.id, 'less');
          await onAction(true);
          toast('Baseline tightened — Wean will stretch you faster.');
        } },
      ],
    }),
  },
  {
    id: 'add_money',
    priority: 20,
    isEligible: (ctx) => !ctx.money?.enabled,
    build: (ctx, onAction) => buildCard({
      title: 'Want to see what this is costing?',
      body: 'A rough estimate is fine — you can fix it later.',
      actions: [
        { label: 'Add quick estimate', onClick: async () => {
          await onAction(true);
          navigate('/plan', { habit: ctx.habit.id, section: 'money' });
        }},
        { label: 'Not now', onClick: () => onAction(false) },
      ],
    }),
  },
  {
    id: 'add_risky_times',
    priority: 30,
    isEligible: (ctx) => (ctx.settings.triggers?.length ?? 0) === 0,
    build: (ctx, onAction) => buildCard({
      title: 'Do urges hit at certain times?',
      body: 'Tag a few — Wean will bias rescue picks toward what helps.',
      actions: [
        { label: 'Tag triggers', onClick: async () => {
          await onAction(true);
          navigate('/plan', { habit: ctx.habit.id, section: 'rescue' });
        }},
        { label: 'Not now', onClick: () => onAction(false) },
      ],
    }),
  },
  {
    id: 'choose_reminders',
    priority: 40,
    isEligible: (ctx) => !ctx.settings.notificationsEnabled,
    build: (_ctx, onAction) => buildCard({
      title: 'Want a discreet reminder?',
      body: 'Optional. Reminders only fire while Wean is open — no servers involved.',
      actions: [
        { label: 'Enable reminders', onClick: async () => {
          await onAction(true);
          navigate('/settings');
        }},
        { label: 'No thanks', onClick: () => onAction(false) },
      ],
    }),
  },
  {
    id: 'easier_harder',
    priority: 50,
    isEligible: (ctx) => {
      const uses = ctx.recentEvents.filter(
        (e) => e.type === 'usage_logged' && e.status !== 'undone',
      ).length;
      return uses >= 5;
    },
    build: (ctx, onAction) => buildCard({
      title: 'Is this week feeling right?',
      body: 'You can tune the target up or down — it\'s learning, not failure.',
      actions: [
        { label: 'Make it easier', onClick: async () => {
          await onAction(true);
          navigate('/plan', { habit: ctx.habit.id, section: 'schedule' });
        }},
        { label: 'Make it harder', onClick: async () => {
          await onAction(true);
          navigate('/plan', { habit: ctx.habit.id, section: 'schedule' });
        }},
        { label: 'About right', onClick: () => onAction(true) },
      ],
    }),
  },
];

/**
 * Apply a "I usually do more/less" answer from the baseline tune-up. Loosens
 * or tightens the active plan state by ~20% so the suggestion engine has a
 * better starting point. Idempotent on top of subsequent adjustments.
 */
async function adjustBaseline(habitId: string, direction: 'more' | 'less'): Promise<void> {
  const st = getState();
  const { plan, planState: ps } = planFor(habitId, st);
  if (!plan || !ps) return;
  const factor = direction === 'more' ? 0.8 : 1.2;
  if (ps.state.planType === 'interval') {
    const cur = ps.state as IntervalState;
    const next: IntervalState = {
      ...cur,
      targetIntervalMs: Math.max(cur.minIntervalMs, Math.min(cur.maxIntervalMs, Math.round(cur.targetIntervalMs * factor))),
      baselineIntervalMs: Math.max(cur.minIntervalMs, Math.round(cur.baselineIntervalMs * factor)),
    };
    await planStates.put({ ...ps, state: next, updatedAt: nowIso() });
  } else if (ps.state.planType === 'quota') {
    const cur = ps.state as QuotaState;
    const nextCap = Math.max(1, Math.round(cur.currentQuota * (direction === 'more' ? 1.2 : 0.8)));
    const next: QuotaState = {
      ...cur,
      currentQuota: nextCap,
      baselineQuota: Math.max(nextCap, Math.round(cur.baselineQuota * (direction === 'more' ? 1.2 : 0.8))),
    };
    await planStates.put({ ...ps, state: next, updatedAt: nowIso() });
  }
  // Window plans have no clean "baseline" knob from a single answer; skip.
  await metaSet('onboarding_metadata', {
    ...(st.meta.onboarding_metadata ?? { version: 2, quickStartCompletedAt: nowIso(), baselineConfidence: 'estimated', selectedGoalStyle: 'unsure', selectedPace: plan.pace, templateCategory: 'other', tuneUpCardsSeen: [], tuneUpCardsCompleted: [] }),
    baselineConfidence: 'known',
  });
}

interface CardSpec {
  title: string;
  body: string;
  actions: Array<{ label: string; onClick: () => void | Promise<void> }>;
}

function buildCard(spec: CardSpec): HTMLElement {
  const card = el('section', {
    class: 'card tune-up-card',
    attrs: { 'aria-label': spec.title, style: 'border-color:var(--accent-soft);' },
  });
  card.appendChild(el('div', {
    class: 'muted',
    attrs: { style: 'text-transform:uppercase;letter-spacing:0.06em;font-size:0.72rem;margin-bottom:0.35rem;' },
  }, 'Tune-up'));
  card.appendChild(el('h2', { attrs: { style: 'margin:0;font-size:1.05rem;' } }, spec.title));
  card.appendChild(el('p', { class: 'muted', attrs: { style: 'margin:0.35rem 0 0.65rem;' } }, spec.body));
  const row = el('div', { class: 'row', attrs: { style: 'flex-wrap:wrap;gap:0.5rem;' } });
  for (const a of spec.actions) {
    row.appendChild(el('button', {
      class: 'btn small', attrs: { type: 'button' },
      onclick: async () => {
        try {
          await a.onClick();
        } catch (err) {
          console.error('Tune-up action failed:', err);
          toast('Could not save — try again.');
        }
      },
    }, a.label));
  }
  card.appendChild(row);
  return card;
}

/**
 * Picks the highest-priority eligible card that hasn't been completed yet.
 * Records that the card was seen on render. Caller renders the returned node.
 * Returns null when no card applies.
 */
export async function pickTuneUpCard(ctx: TuneUpContext): Promise<HTMLElement | null> {
  const meta = ctx.metadata;
  if (!meta || meta.version !== 2) return null;
  const completed = new Set(meta.tuneUpCardsCompleted);
  const sorted = [...CARDS].sort((a, b) => a.priority - b.priority);
  const choice = sorted.find((c) => !completed.has(c.id) && c.isEligible(ctx));
  if (!choice) return null;
  // Mark seen on render. Idempotent.
  if (!meta.tuneUpCardsSeen.includes(choice.id)) {
    const updated: OnboardingMetadata = {
      ...meta,
      tuneUpCardsSeen: [...meta.tuneUpCardsSeen, choice.id],
    };
    await metaSet('onboarding_metadata', updated);
  }
  return choice.build(ctx, async (_completedCard) => {
    const fresh = getState().meta.onboarding_metadata;
    if (!fresh) return;
    // Both "did it" and "Not now" retire the card. Dismissal is a decision —
    // we don't want to nag the user with the same suggestion on every render.
    // The user can still reach each feature manually from the relevant
    // section of Plan or Settings.
    const next: OnboardingMetadata = {
      ...fresh,
      tuneUpCardsCompleted: Array.from(new Set([...fresh.tuneUpCardsCompleted, choice.id])),
    };
    await metaSet('onboarding_metadata', next);
    window.dispatchEvent(new CustomEvent('wean:state-changed'));
  });
}
